import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2 } from "lucide-react";

const TIER_LABEL = { free: "Basic", intermediate: "Intermediate", pro: "Pro" };

export default function SubscriptionSuccess() {
  const { user, refreshUser } = useAuth();
  const [params] = useSearchParams();
  const isDowngrade = params.get("downgrade") === "1";
  const [state, setState] = useState(isDowngrade ? "done" : "checking"); // checking | done
  const [tier, setTier] = useState(isDowngrade ? "free" : (user?.tier || "free"));
  const attempts = useRef(0);

  useEffect(() => {
    if (isDowngrade) {
      api.get("/auth/me").then(({ data }) => { if (data.user) refreshUser(data.user); }).catch(() => {});
      return;
    }
    let active = true;
    const poll = async () => {
      if (!active) return;
      attempts.current += 1;
      try {
        const { data } = await api.post("/subscription/sync");
        if (data.user) {
          refreshUser(data.user);
          setTier(data.user.tier);
          if (data.user.tier && data.user.tier !== "free") { setState("done"); return; }
        }
      } catch (e) { console.error("Subscription sync poll failed", e); }
      if (attempts.current >= 5) { setState("done"); return; }
      setTimeout(poll, 2500);
    };
    poll();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-lg mx-auto px-4 sm:px-6 py-24 text-center" data-testid="subscription-success-page">
      {state === "checking" ? (
        <>
          <Loader2 className="w-14 h-14 text-primary mx-auto animate-spin" />
          <h1 className="font-display text-3xl font-semibold mt-6">Activating your plan…</h1>
          <p className="text-muted-foreground mt-2">We're syncing your subscription. This only takes a moment.</p>
        </>
      ) : (
        <>
          <CheckCircle2 className="w-16 h-16 text-primary mx-auto" strokeWidth={1.5} />
          <h1 className="font-display text-3xl font-semibold mt-4" data-testid="subscription-active-heading">
            {isDowngrade
              ? "You're now on the Basic plan"
              : `You're on the ${TIER_LABEL[tier] || tier} plan`}
          </h1>
          <p className="text-muted-foreground mt-2" data-testid="subscription-active-tier">
            {isDowngrade
              ? "Your paid subscription has been canceled. You can upgrade again anytime."
              : "Your account has been upgraded. Enjoy your new limits and features."}
          </p>
          <div className="flex gap-3 justify-center mt-8">
            <Link to="/dashboard"><Button className="rounded-sm" data-testid="go-to-dashboard-btn">Go to dashboard</Button></Link>
            <Link to="/pricing"><Button variant="outline" className="rounded-sm">View plans</Button></Link>
          </div>
        </>
      )}
    </div>
  );
}
